import { useEffect, useRef, useState, type FormEvent } from 'react';

import { AlertCircle, ArrowRight, Lock, Mail } from 'lucide-react';

import { ApiError } from '@/api/client';
import { BrandLockup } from '@/components/brand/BrandLockup';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Field } from '@/components/ui/Field';
import { Input, PasswordInput } from '@/components/ui/Input';

import { useLogin } from '../api';
import { AdministratorDialog } from '../components/AdministratorDialog';
import { clearSessionEnded, sessionEndedPending } from '../sessionEnded';
import styles from './LoginPage.module.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Decorative rows in the brand panel. Purely illustrative, no real data. */
const LEDGER_ROWS = [
  { initials: 'AR', tone: 'a', width: 58, pill: 'Screening' },
  { initials: 'MK', tone: 'b', width: 74, pill: 'Documents' },
  { initials: 'SD', tone: 'c', width: 46, pill: 'Interview' },
  { initials: 'JP', tone: 'd', width: 66, pill: 'Review' },
] as const;

// `| undefined` explicitly: clearing a field error assigns undefined, and
// exactOptionalPropertyTypes treats that as different from omitting the key.
type FieldErrors = { email?: string | undefined; password?: string | undefined };

export function LoginPage() {
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [capsLock, setCapsLock] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  /**
   * The message shown when a session ends mid-session rather than at the door.
   *
   * Without it, an expiry looks like being mysteriously logged out.
   *
   * Not inferred from the route we were redirected from. The guard attaches
   * `from` to every redirect, including a first-time visitor's, so the old
   * `Boolean(from) && from !== HOME_PATH` announced an ended session to someone
   * who had never had one — the site root gave `from === '/'`, which is not
   * HOME_PATH, so the banner showed on every fresh visit.
   *
   * Read once on mount, then cleared in an effect rather than in the
   * initialiser. `<StrictMode>` double-invokes state initialisers in
   * development to surface impure ones, so a read-and-clear would give a
   * different answer on the second invocation and the banner would behave
   * differently in dev and in production.
   */
  const [expired] = useState(sessionEndedPending);
  useEffect(() => {
    // Idempotent, which matters: StrictMode runs effects twice in development.
    if (expired) clearSessionEnded();
  }, [expired]);

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!email.trim()) next.email = 'Enter your email address.';
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Enter a valid email address.';
    if (!password) next.password = 'Enter your password.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    // No navigation here on purpose. Seeding the signed-in user is enough: the route
    // guard sees it and redirects, to the intended route if there was one. Doing it
    // in both places is a race, and the guard wins it.
    login.mutate({ email: email.trim(), password, remember });
  };

  const formError = login.error instanceof ApiError ? login.error.message : null;

  return (
    <div className={styles.page}>
      {/* ------------------------------------------------------ brand -- */}
      <aside className={styles.brand}>
        <div className={styles.grid} aria-hidden="true" />

        <div className={styles.mark}>
          <BrandLockup height={80} />
        </div>

        <div className={styles.pitch}>
          <h1 className={styles.pitchTitle}>Every candidate, every stage, in one place.</h1>
          <p className={styles.pitchBody}>
            Track candidates from first application onward: profiles, documents and status
            updates held in one shared record instead of scattered across inboxes and
            spreadsheets.
          </p>

          <div className={styles.ledger} aria-hidden="true">
            <div className={styles.ledgerHead}>
              <span className={`${styles.dot} ${styles.dotLive}`} />
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.ledgerLabel}>Candidates</span>
            </div>
            {LEDGER_ROWS.map((row, index) => (
              <div
                key={row.initials}
                className={styles.ledgerRow}
                style={{ animationDelay: `${360 + index * 110}ms` }}
              >
                <span className={`${styles.ledgerAvatar} ${styles[`tone_${row.tone}`]}`}>
                  {row.initials}
                </span>
                <span className={styles.ledgerBar} style={{ inlineSize: `${row.width}%` }} />
                <span className={styles.ledgerPill}>{row.pill}</span>
              </div>
            ))}
          </div>
        </div>

        <p className={styles.brandFoot}>
          Authorised users only. Activity on this system is logged.
        </p>
      </aside>

      {/* ------------------------------------------------------- form -- */}
      <main className={styles.formSide}>
        <div className={styles.formWrap}>
          <div className={styles.mobileMark}>
            {/* On the white form side, so the inked variant. */}
            <BrandLockup height={56} tone="light" />
          </div>

          <h2 className={styles.title}>Sign in</h2>
          <p className={styles.subtitle}>Use your work email to access the portal.</p>

          {expired ? (
            <Alert tone="warning" className={styles.banner}>
              Your session ended. Sign in again to carry on.
            </Alert>
          ) : null}

          {formError ? (
            <Alert tone="danger" className={styles.banner}>
              {formError}
            </Alert>
          ) : null}

          <form onSubmit={submit} noValidate>
            <Field label="Email address" error={errors.email}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  ref={emailRef}
                  describedBy={describedBy}
                  invalid={invalid}
                  type="email"
                  autoComplete="username"
                  placeholder="name@flyst.com"
                  icon={<Mail size={16} />}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrors((current) => ({ ...current, email: undefined }));
                  }}
                />
              )}
            </Field>

            <Field
              label="Password"
              error={errors.password}
              // Caps Lock accounts for a good share of "the password is wrong", and
              // the field is masked so there is no way to see it.
              hint={
                capsLock ? (
                  <span className={styles.caps}>
                    <AlertCircle size={13} aria-hidden="true" /> Caps Lock is on.
                  </span>
                ) : undefined
              }
            >
              {({ id, describedBy, invalid }) => (
                <PasswordInput
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  icon={<Lock size={16} />}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setErrors((current) => ({ ...current, password: undefined }));
                  }}
                  onKeyUp={(event) => {
                    setCapsLock(event.getModifierState('CapsLock'));
                  }}
                  onKeyDown={(event) => {
                    setCapsLock(event.getModifierState('CapsLock'));
                  }}
                />
              )}
            </Field>

            <div className={styles.meta}>
              <Checkbox
                label="Keep me signed in"
                checked={remember}
                onChange={(event) => {
                  setRemember(event.target.checked);
                }}
              />
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              size="lg"
              block
              loading={login.isPending}
              iconAfter={<ArrowRight size={16} />}
            >
              {login.isPending ? 'Signing in' : 'Sign in'}
            </Button>
          </form>

          <p className={styles.legal}>
            Accounts are created by an administrator. There is no self-service registration.
          </p>
        </div>
      </main>

      <AdministratorDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      />
    </div>
  );
}
